<template>
  <v-menu
    ref="menu"
    v-model="isMenuVisible"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    min-width="290px"
    :close-on-click="false"
  >
    <template v-slot:activator="{ on, attrs }">
      <div class="date-picker">
        <div class="date-picker__input" v-bind="attrs" v-on="on">
          <v-icon size="22px" color="primary" class="mr-2">
            icon-calendar
          </v-icon>
          {{ selectedFilters.option.name }}
          <v-icon size="18px" color="black">
            icon-chevron
          </v-icon>
        </div>
        <div class="date-picker__label" v-bind="attrs" v-on="on">
          <span> {{ selectedDateRange }}</span>
        </div>
        <div v-if="selectedFilters.comparison" class="date-picker__sublabel">
          {{ $t("message.reviews.categories.comparedTo") }}:
          <span>{{ selectedFilters.comparison.name }}</span>
        </div>
      </div>
    </template>
    <v-card class="date-picker__dropdown">
      <div
        v-for="filter in filterOptions"
        :key="filter.value"
        :class="{ 'is-active': filter.id === currentOption.id }"
        class="date-picker__option"
        @click="setFilterOption(filter)"
      >
        {{ filter.name }}
        <v-icon v-if="filter.id === currentOption.id" color="primary">
          icon-check
        </v-icon>
      </div>
      <template>
        <v-divider></v-divider>
        <div class="date-picker__option date-picker__option--disable-hover">
          {{ $t("message.reviews.categories.compare") }}
          <v-switch
            v-model="isComparable"
            :disabled="isCompareDisabled"
            class="date-picker__switch"
            inset
            hide-details
            @change="onIsComparableChange"
          ></v-switch>
        </div>
        <v-divider></v-divider>
        <template v-if="isComparable">
          <div
            v-for="item in compareOptions"
            :key="item.value"
            class="date-picker__option"
            :class="{
              'is-active': compareOption && item.id === compareOption.id
            }"
            @click="addToComparison(item)"
          >
            <span>{{ item.name }}</span>
            <v-icon
              v-if="compareOption && item.id === compareOption.id"
              color="primary"
            >
              icon-check
            </v-icon>
          </div>
        </template>
      </template>
      <div class="date-picker__actions">
        <v-btn text @click="isMenuVisible = false">
          {{ $t("message.common.cancel") }}</v-btn
        >
        <v-btn text color="primary" @click="saveFilters">{{
          $t("message.common.save")
        }}</v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { PERIOD } from "@/enums/period.enum";
import { FORMAT } from "@/config/date-format";
import moment from "moment";
import { mapState } from "vuex";
import {
  filterOptions,
  compareOptions
} from "@/config/categories-filter-options";
import { GTM_PERIOD_TYPE_ACTION } from "@/enums/gtm-events";
import { COMPARE_OPTIONS_ENUM } from "@/enums/compare-options.enum";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "ReviewsCategoriesDatePicker",
  props: {
    preselectDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      FEATURE_FLAG,
      isCompareDisabled: false,
      isComparable: false,
      isMenuVisible: false,
      filterOptions: filterOptions(),
      compareOptions: compareOptions(),
      currentOption: null,
      rangeOption: null,
      compareOption: null,
      selectedDate: null,
      selectedFilters: { option: null, comparison: null },
      selectedDateRange: null,
      currentDate: null,
      singleDatePeriods: [PERIOD.YEARS, PERIOD.WEEKS, PERIOD.QUARTER],
      completeDate: {
        date_from: "",
        date_to: ""
      },
      generalPeriodTypes: {
        [PERIOD.DAYS]: GTM_PERIOD_TYPE_ACTION.YESTERDAY,
        [PERIOD.MONTHS]: GTM_PERIOD_TYPE_ACTION.PREVIOUS_MONTH,
        [PERIOD.QUARTER]: GTM_PERIOD_TYPE_ACTION.PREVIOUS_QUARTER,
        [PERIOD.YEARS]: GTM_PERIOD_TYPE_ACTION.PREVIOUS_YEAR,
        [PERIOD.WEEKS]: GTM_PERIOD_TYPE_ACTION.PREVIOUS_WEEK
      }
    };
  },

  computed: {
    ...mapState({
      reviews: state => state.reviews.data,
      loading: state => state.reviews.loading,
      selectedLocale: state => state.settings.selectedLocale
    }),
    isDifferentYear() {
      return (
        this.selectedDate.date_from.format(FORMAT.YEAR) !==
        moment(Date.now()).format(FORMAT.YEAR)
      );
    }
  },

  created() {
    if (this.$route.query.dateType === "full") {
      this.setComparisonDisabled();
    }

    let preselectedOption;
    if (!this.preselectDisabled) {
      preselectedOption =
        this.filterOptions.find(
          item =>
            this.$route.query.dateType == item.type &&
            this.$route.query.dateQuantity == item.quantity
        ) || this.filterOptions[2];
    } else {
      preselectedOption = this.filterOptions[2];
    }
    this.rangeOption = this.currentOption = this.selectedFilters.option = preselectedOption;
    this.selectedDate = {
      date_from: moment().subtract(
        this.selectedFilters.option.quantity,
        this.selectedFilters.option.type
      ),
      date_to: moment(Date.now()).subtract(1, "days")
    };
    if (this.$route.query.compareOption && !this.preselectDisabled) {
      this.isComparable = true;
      this.compareOption = this.compareOptions.find(
        item => item.value == this.$route.query.compareOption
      );
      this.selectedFilters.comparison = this.compareOption;
    }
    this.prepareDate();
    this.setDateRange();
  },
  watch: {
    isMenuVisible(current) {
      if (current) return;
      this.hideFilters();
    }
  },
  methods: {
    prepareDate() {
      const {
        option: { type, quantity },
        comparison
      } = this.selectedFilters;

      this.selectedDate = this.getSelectedDate(type, quantity, comparison);
    },
    emitSelectedDate() {
      const isRangeSelected =
        this.selectedDate.date_from && this.selectedDate.date_to;
      this.$emit(
        "change",
        isRangeSelected
          ? {
              ...(this.getEventPeriodType(
                this.selectedFilters.option.type,
                this.selectedFilters.option.quantity
              ) !== "all" && {
                date_from: this.selectedDate.date_from.format(FORMAT.DATE),
                date_to: this.selectedDate.date_to.format(FORMAT.DATE)
              }),

              ...(this.isComparable && {
                compare_to: this.compareOption.value,
                comparison: this.selectedDate.comparison
              }),
              period_type: this.getEventPeriodType(
                this.selectedFilters.option.type,
                this.selectedFilters.option.quantity
              )
            }
          : {}
      );
    },
    isFeatureFlagEnabled,
    onIsComparableChange() {
      if (!this.isComparable) {
        this.compareOption = null;
        return;
      }
      this.compareOption = this.compareOptions[0];
    },
    getSelectedDateRange(dateFormat, range = false) {
      const { date_from: dateFrom, date_to: dateTo } = this.selectedDate;

      if (!range) {
        return dateFrom.format(dateFormat);
      } else if (range) {
        return `${dateFrom.format(dateFormat)} - ${dateTo.format(dateFormat)}`;
      }
    },
    getDateFormat(type, quantity) {
      if (this.isDifferentYear || (quantity === 1 && type === PERIOD.DAYS)) {
        return FORMAT.MMM_DD_YYYY;
      }
      if (type === PERIOD.MONTHS) {
        return quantity === 1 ? FORMAT.MMMM_YYYY : FORMAT.MMM_DD;
      }

      return FORMAT.MMM_DD;
    },
    setDateRange() {
      const {
        option: { quantity, type }
      } = this.selectedFilters;

      if (!quantity) {
        this.selectedDateRange = "";
        return;
      }
      if (quantity === 1 && !this.singleDatePeriods.includes(type)) {
        this.selectedDateRange = this.getSelectedDateRange(
          this.getDateFormat(type, quantity)
        );
      } else {
        this.selectedDateRange = this.getSelectedDateRange(
          this.getDateFormat(type, quantity),
          true
        );
      }
    },
    setComparisonDisabled() {
      this.isComparable = false;
      this.isCompareDisabled = true;
      this.compareOption = null;
    },
    setFilterOption(value) {
      if (value.type === PERIOD.FULL) {
        this.setComparisonDisabled();
      } else {
        this.isCompareDisabled = false;
      }
      this.currentOption = value;
    },
    hideFilters() {
      this.currentOption = this.rangeOption;
      this.selectedFilters.option = this.rangeOption;
    },

    getBasicSelectedDate(type, quantity) {
      if (type === PERIOD.DAY) {
        const yesterday = moment(Date.now()).subtract(
          1,
          this.selectedFilters.option.type
        );
        const isPreviousDay = quantity > 1;

        return {
          date_from: moment(isPreviousDay ? yesterday : Date.now()).subtract(
            isPreviousDay ? quantity - 1 : quantity,
            this.selectedFilters.option.type
          ),
          date_to: yesterday
        };
      } else if (type === PERIOD.FULL) {
        return this.completeDate;
      } else {
        return {
          date_from: moment()
            .subtract(quantity, this.selectedFilters.option.type)
            .startOf(type),
          date_to: moment()
            .subtract(1, this.selectedFilters.option.type)
            .endOf(type)
        };
      }
    },
    getSelectedDate(type, quantity, comparison) {
      if (type === PERIOD.FULL) return this.completeDate;
      this.completeDate = this.getBasicSelectedDate(type, quantity);

      if (!comparison) {
        return this.completeDate;
      }

      if (comparison.value === COMPARE_OPTIONS_ENUM.PREVIOUS_YEAR) {
        this.completeDate.comparison = this.getComparisonDateRange(
          this.completeDate,
          PERIOD.YEAR,
          1
        );
      } else if (comparison.value === COMPARE_OPTIONS_ENUM.PREVIOUS_PERIOD) {
        this.completeDate.comparison = this.getComparisonDateRange(
          this.completeDate,
          type,
          quantity
        );
      }

      return this.completeDate;
    },
    getComparisonDateRange(date, type, quantity) {
      return {
        date_from: moment(date.date_from)
          .subtract(quantity, type)
          .format(FORMAT.DATE),
        date_to: moment(date.date_to)
          .subtract(quantity, type)
          .format(FORMAT.DATE)
      };
    },
    saveFilters() {
      this.rangeOption = this.currentOption;
      this.selectedFilters.option = this.rangeOption;
      this.selectedFilters.comparison = this.compareOption;
      this.$store.dispatch(
        "reviewsCategoriesAnalytics/setIsComparing",
        this.isComparable
      );
      this.$router.push({
        query: {
          ...this.$route.query,
          dateType: this.rangeOption.type,
          dateQuantity: this.rangeOption.quantity,
          ...(this.compareOption && {
            compareOption: this.compareOption.value
          })
        }
      });
      this.prepareDate();
      this.setDateRange();
      this.emitSelectedDate();

      this.isMenuVisible = false;
    },
    getEventPeriodType(type, quantity) {
      if (quantity === 1) {
        return this.generalPeriodTypes[type];
      }
      if (type == PERIOD.DAYS) {
        return `last_${quantity}d`;
      } else if (type == PERIOD.MONTHS) {
        return `previous_${quantity}m`;
      } else if (type == PERIOD.QUARTER) {
        return `previous_${quantity}q`;
      } else if (type == PERIOD.FULL) {
        return GTM_PERIOD_TYPE_ACTION.ALL;
      }
    },
    addToComparison(item) {
      this.compareOption = item;
    }
  }
};
</script>
<style lang="scss" scoped>
.date-picker {
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  &__input {
    padding: 0;
    font-weight: 600;
    display: flex;
    align-items: center;
  }

  &__label {
    margin-top: $spacing * 0.5;
    margin-right: $spacing * 3;
    font-weight: 400;
    display: flex;
    align-items: center;
    text-transform: capitalize;

    span {
      margin-left: $spacing;
    }
  }

  &__sublabel {
    margin-top: $spacing * 0.5;
    @include font-size(12px);

    span {
      font-weight: 600;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: $spacing * 2;

    ::v-deep .v-btn {
      text-transform: capitalize;
      letter-spacing: unset;
    }
  }

  &__dropdown {
    padding: $spacing * 3.5;
    display: flex;
    flex-direction: column;
  }

  &__option {
    text-align: left;
    @include font-size(14px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $spacing * 1.5 0;

    &.is-active {
      color: $primary;
      font-weight: 700;
      justify-content: flex-start;

      ::v-deep .v-icon {
        font-weight: 700;
        margin-left: $spacing * 2.5;
      }
    }

    &:not(&--disable-hover):hover {
      cursor: pointer;
      font-weight: 700;
    }

    & + & {
      margin-top: $spacing * 0.5;
    }
  }

  &__switch {
    margin: 0 0 0 $spacing;
    padding: 0;
  }
}
</style>
